import {wsTrans} from './utils';
import {globs, cl} from '../../components/utils/utils';

export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
export const AUTH_MODES = [
  {v: 'apiKeySecret', t: 'API key + secret'},
  {v: 'oauth2', t: 'OAuth 2.0'},
];
export const GRANT_TYPES = [
  {v: 'client_credentials', t: 'Client credentials'},
  {v: 'authorization_code', t: 'Authorization code'},
  {v: 'password', t: 'Password'},
  {v: 'refresh_token', t: 'Refresh token'},
];

export const WIZARD_STEPS = [
  {id: 0, title: 'Connection', subtitle: 'Endpoint and authentication'},
  {id: 1, title: 'Site & zone', subtitle: 'Choose where to attach data'},
  {id: 2, title: 'Test', subtitle: 'Verify the API connection'},
  {id: 3, title: 'Response path', subtitle: 'Pick the root from the API response'},
];

export const DEFAULT_CONNECTOR_CONFIG = {
  name: '',
  baseUrl: '',
  method: 'GET',
  timeout: 5000,
  queryParams: [{key: '', value: ''}],
  bodyParams: [{key: '', value: ''}],
  authMode: 'apiKeySecret',
  apiKey: '',
  apiSecret: '',
  tokenUrl: '',
  clientId: '',
  clientSecret: '',
  scopes: '',
  grantType: 'client_credentials',
  siteId: '',
  zoneId: '',
  responseRootPath: '',
  pollInterval: 300,
};

const normalizeConfig = (config = {}) => {
  const merged = Object.assign({}, DEFAULT_CONNECTOR_CONFIG, config);
  if (merged.apiKeySecret_key && !merged.apiKey) merged.apiKey = merged.apiKeySecret_key;
  if (merged.apiKeySecret_secret && !merged.apiSecret) merged.apiSecret = merged.apiKeySecret_secret;
  if (merged.authMode === 'apiKey') merged.authMode = 'apiKeySecret';
  if (!Array.isArray(merged.queryParams) || !merged.queryParams.length) {
    merged.queryParams = [{key: '', value: ''}];
  }
  if (!Array.isArray(merged.bodyParams) || !merged.bodyParams.length) {
    merged.bodyParams = [{key: '', value: ''}];
  }
  return merged;
};

export const methodUsesBody = (method) => ['POST', 'PUT', 'PATCH'].includes(method || 'GET');

export const paramsToObject = (rows = []) => {
  const out = {};
  rows.forEach((row) => {
    const key = String(row.key || '').trim();
    if (!key) return;
    out[key] = row.value != null ? String(row.value) : '';
  });
  return out;
};

export const validateParamRows = (rows = [], prefix = 'params') => {
  const errors = {};
  rows.forEach((row, i) => {
    const hasKey = isNonEmpty(row.key);
    const hasValue = isNonEmpty(row.value);
    if (hasKey && !hasValue) errors[`${prefix}.${i}`] = 'Parameter value is required.';
    if (!hasKey && hasValue) errors[`${prefix}.${i}`] = 'Parameter name is required.';
  });
  return errors;
};

export const cloneConnectorConfig = (config = {}) => (
  JSON.parse(JSON.stringify(normalizeConfig(config)))
);

const isNonEmpty = (val) => String(val ?? '').trim().length > 0;

export const isBaseUrlValid = (baseUrl) => {
  const trimmed = String(baseUrl || '').trim();
  if (!trimmed) return false;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (e) {
    return false;
  }
};

export const areCredentialsComplete = (config) => {
  const c = normalizeConfig(config);
  switch (c.authMode) {
    case 'apiKeySecret':
      return isNonEmpty(c.apiKey) && isNonEmpty(c.apiSecret);
    case 'oauth2':
      return isNonEmpty(c.tokenUrl) && isNonEmpty(c.clientId) && isNonEmpty(c.clientSecret);
    default:
      return false;
  }
};

export const getPreflightChecklist = (config) => ({
  baseUrl: isBaseUrlValid(config.baseUrl),
  credentials: areCredentialsComplete(config),
});

export const isTestReady = (config) => {
  const checklist = getPreflightChecklist(config);
  return checklist.baseUrl && checklist.credentials
    && isNonEmpty(config.siteId) && isNonEmpty(config.zoneId);
};

export const getValidationErrors = (config, action = 'test') => {
  const c = normalizeConfig(config);
  const errors = {};
  if (!isBaseUrlValid(c.baseUrl)) {
    errors.baseUrl = 'Base URL is required and must be a valid http(s) URL.';
  }
  if (c.timeout === '' || c.timeout == null || Number(c.timeout) <= 0) {
    errors.timeout = 'Timeout must be a positive number.';
  }
  Object.assign(errors, validateParamRows(c.queryParams, 'queryParams'));
  if (methodUsesBody(c.method)) {
    Object.assign(errors, validateParamRows(c.bodyParams, 'bodyParams'));
  }
  switch (c.authMode) {
    case 'apiKeySecret':
      if (!isNonEmpty(c.apiKey)) errors.apiKey = 'API key is required.';
      if (!isNonEmpty(c.apiSecret)) errors.apiSecret = 'API secret is required.';
      break;
    case 'oauth2':
      if (!isNonEmpty(c.tokenUrl)) errors.tokenUrl = 'Token URL is required.';
      else if (!isBaseUrlValid(c.tokenUrl)) errors.tokenUrl = 'Token URL must be a valid http(s) URL.';
      if (!isNonEmpty(c.clientId)) errors.clientId = 'Client ID is required.';
      if (!isNonEmpty(c.clientSecret)) errors.clientSecret = 'Client secret is required.';
      break;
    default:
      break;
  }
  if (action === 'test' || action === 'save') {
    if (!isNonEmpty(c.siteId)) errors.siteId = 'Site is required.';
    if (!isNonEmpty(c.zoneId)) errors.zoneId = 'Zone is required.';
  }
  if (action === 'save') {
    if (!isNonEmpty(c.name)) errors.name = 'Connector name is required.';
    if (c.pollInterval === '' || c.pollInterval == null || Number(c.pollInterval) <= 0) {
      errors.pollInterval = 'Poll interval must be a positive number.';
    }
  }
  return errors;
};

export const getStepValidationErrors = (config, step) => {
  const c = normalizeConfig(config);
  switch (step) {
    case 0: {
      const errors = {};
      if (!isBaseUrlValid(c.baseUrl)) errors.baseUrl = 'Base URL is required.';
      if (c.timeout === '' || c.timeout == null || Number(c.timeout) <= 0) {
        errors.timeout = 'Timeout must be a positive number.';
      }
      Object.assign(errors, validateParamRows(c.queryParams, 'queryParams'));
      if (methodUsesBody(c.method)) {
        Object.assign(errors, validateParamRows(c.bodyParams, 'bodyParams'));
      }
      if (c.authMode === 'apiKeySecret') {
        if (!isNonEmpty(c.apiKey)) errors.apiKey = 'API key is required.';
        if (!isNonEmpty(c.apiSecret)) errors.apiSecret = 'API secret is required.';
      }
      if (c.authMode === 'oauth2') {
        if (!isNonEmpty(c.tokenUrl)) errors.tokenUrl = 'Token URL is required.';
        if (!isNonEmpty(c.clientId)) errors.clientId = 'Client ID is required.';
        if (!isNonEmpty(c.clientSecret)) errors.clientSecret = 'Client secret is required.';
      }
      return errors;
    }
    case 1: {
      const errors = {};
      if (!isNonEmpty(c.siteId)) errors.siteId = 'Select a site.';
      if (!isNonEmpty(c.zoneId)) errors.zoneId = 'Select a zone.';
      if (!isNonEmpty(c.name)) errors.name = 'Connector name is required.';
      if (c.pollInterval === '' || c.pollInterval == null || Number(c.pollInterval) <= 0) {
        errors.pollInterval = 'Poll interval must be greater than zero.';
      }
      return errors;
    }
    default:
      return {};
  }
};

const getConnectorName = (config) => {
  if (isNonEmpty(config.name)) return String(config.name).trim();
  try {
    return new URL(String(config.baseUrl || '').trim()).hostname || 'API Connector';
  } catch (e) {
    return 'API Connector';
  }
};

export const buildThirdPartyApiPayload = (config) => {
  const c = normalizeConfig(config);
  const session = globs.userData && globs.userData.session ? globs.userData.session : {};
  const isOAuth = c.authMode === 'oauth2';
  return {
    accountId: session.accountId,
    siteId: c.siteId,
    zoneId: c.zoneId,
    name: getConnectorName(c),
    baseUrl: String(c.baseUrl || '').trim(),
    method: c.method || 'GET',
    queryParams: paramsToObject(c.queryParams),
    bodyParams: methodUsesBody(c.method) ? paramsToObject(c.bodyParams) : {},
    authType: isOAuth ? 'oauth2' : 'apiKeySecret',
    apiKey: isOAuth ? c.clientId : c.apiKey,
    apiSecret: isOAuth ? c.clientSecret : c.apiSecret,
    responePath: c.responseRootPath || '',
    pollInterval: Number(c.pollInterval) || 300,
  };
};

export const formatLeafValue = (value) => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') {
    return value.length > 48 ? `"${value.slice(0, 48)}…"` : `"${value}"`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `[${value.length} items]`;
  if (typeof value === 'object') return '{object}';
  return String(value);
};

export const buildResponseTreeNodes = (data, path = '', label = '') => {
  if (data === null || data === undefined || typeof data !== 'object') {
    return [{
      path: path || 'value',
      label: label || 'value',
      isLeaf: true,
      value: data,
      displayValue: formatLeafValue(data),
      children: [],
    }];
  }

  if (Array.isArray(data)) {
    if (!data.length) {
      return [{
        path: path || label || '[]',
        label: label || '[]',
        isLeaf: true,
        value: data,
        displayValue: '[]',
        children: [],
      }];
    }
    const childLimit = Math.min(data.length, 25);
    const children = [];
    for (let i = 0; i < childLimit; i++) {
      const childPath = path ? `${path}.${i}` : `${i}`;
      children.push(...buildResponseTreeNodes(data[i], childPath, `[${i}]`));
    }
    return [{
      path: path || label || 'array',
      label: label || 'array',
      isLeaf: false,
      value: data,
      displayValue: `[${data.length} items]`,
      children,
    }];
  }

  if (!path) {
    return Object.keys(data).flatMap((key) => buildResponseTreeNodes(data[key], key, key));
  }

  const children = Object.keys(data).flatMap((key) => {
    const childPath = `${path}.${key}`;
    return buildResponseTreeNodes(data[key], childPath, key);
  });

  return [{
    path,
    label: label || path.split('.').pop(),
    isLeaf: false,
    value: data,
    displayValue: '{object}',
    children,
  }];
};

const redactPayloadForLog = (body) => {
  const copy = JSON.parse(JSON.stringify(body));
  if (copy.apiKey) copy.apiKey = '***';
  if (copy.apiSecret) copy.apiSecret = '***';
  return copy;
};

const extractErrorMessage = (r) => {
  if (!r) return 'Request failed.';
  if (typeof r.message === 'string' && r.message.trim()) return r.message;
  if (typeof r.error === 'string' && r.error.trim()) return r.error;
  if (r.error && typeof r.error === 'object') return JSON.stringify(r.error);
  if (r.data) {
    if (typeof r.data === 'string' && r.data.trim()) return r.data;
    if (typeof r.data.error === 'string' && r.data.error.trim()) return r.data.error;
    if (typeof r.data.message === 'string' && r.data.message.trim()) return r.data.message;
    if (typeof r.data.detail === 'string' && r.data.detail.trim()) return r.data.detail;
    if (r.data.error && typeof r.data.error === 'object') return JSON.stringify(r.data.error);
  }
  if (r.result && r.result !== 'ok') return `Server returned: ${r.result}`;
  return 'Request failed.';
};

const extractTestResponseData = (response) => {
  if (!response || typeof response !== 'object') return response;
  if (response.body != null) return response.body;
  if (response.response != null) return response.response;
  if (response.data != null && response.status != null) return response.data;
  return response;
};

const extractTestResponseStatus = (response, fallback = 200) => {
  if (!response || typeof response !== 'object') return fallback;
  if (response.status != null) return Number(response.status) || fallback;
  if (response.httpStatus != null) return Number(response.httpStatus) || fallback;
  return fallback;
};

export const testConnection = async (config) => {
  try {
    const body = buildThirdPartyApiPayload(config);
    const sessionId = globs.userData && globs.userData.session
      ? globs.userData.session.sessionId
      : undefined;
    const request = {
      cmd: 'cRest',
      uri: '/s/thirdPartyApi',
      method: 'update',
      sessionId,
      body,
    };

    cl('[API Connector] test request', {
      uri: request.uri,
      method: request.method,
      sessionId: sessionId ? 'present' : 'missing',
      body: redactPayloadForLog(body),
    });

    const r = await wsTrans('usa', request);

    cl('[API Connector] test response (raw)', r);

    if (r.result === 'ok') {
      const data = extractTestResponseData(r.data);
      const status = extractTestResponseStatus(r.data, 200);
      cl('[API Connector] test success', {status, data});
      return {ok: true, status, data};
    }

    const data = extractTestResponseData(r.data);
    const status = extractTestResponseStatus(r.data, 0);
    const error = extractErrorMessage(r);
    cl('[API Connector] test failed', {
      result: r.result,
      status,
      error,
      data,
      raw: r,
    });
    return {ok: false, status, data, error};
  } catch (e) {
    cl('[API Connector] test exception', e);
    return {ok: false, status: 0, error: e.message || String(e)};
  }
};

export const STORAGE_KEY = 'link4_api_connector_config';

export const loadStoredConnectorConfig = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return cloneConnectorConfig();
    return cloneConnectorConfig(JSON.parse(raw));
  } catch (e) {
    return cloneConnectorConfig();
  }
};

export const saveStoredConnectorConfig = (config) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeConfig(config)));
};
